// Forum.jsx — главная страница форума.
// Левая колонка: список категорий (карточки).
// Правая колонка: поиск + последние сообщения + статистика.
// Бизнес-логика: AdminService.getForumCategories + getForumStats,
// открытие CreateTopicModal, навигация на «Мои вопросы» и категории.

import React, { useState, useEffect } from 'react';
import AdminService from '../../../API/AdminService';
import CreateTopicModal from './CreateTopicModal';
import { useNavigate } from 'react-router-dom';

import cl from './Forum.module.css';
import ForumSearch from './ForumSearch';
import PageIntro from '../../../components/PageIntro/PageIntro';
import logger from '../../../utils/logger';
import {
    IconMessage, IconPlus, IconUser, IconRefresh, IconAlert,
    IconArrowRight, IconClock,
} from '../../../components/Icons';

// Цвета rail-баров для категорий — поочерёдно из палитры.
const CATEGORY_TONES = ['blue', 'green', 'gold', 'violet', 'cyan', 'red'];

const Forum = () => {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [forumStats, setForumStats] = useState({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);

    useEffect(() => {
        loadForumData();
    }, []);

    const loadForumData = async () => {
        try {
            setLoading(true);
            const [categoriesRes, statsRes] = await Promise.all([
                AdminService.getForumCategories(),
                AdminService.getForumStats(),
            ]);

            setCategories(categoriesRes.data || []);
            setForumStats(statsRes.data || {});
            setError('');
        } catch (err) {
            setError('Ошибка загрузки данных форума');
            logger.error('Ошибка загрузки форума:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleTopicCreated = () => {
        setShowCreateModal(false);
        loadForumData();
    };

    if (loading) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.loadingContainer}>
                        <div className={cl.spinner}/>
                        <p>Загрузка форума…</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={cl.page_wrapper}>
                <div className={cl.wrap_content_1}>
                    <div className={cl.errorState}>
                        <span className={cl.errorIcon}>
                            <IconAlert width={20} height={20}/>
                        </span>
                        <h3>Не удалось загрузить форум</h3>
                        <p>{error}</p>
                        <button onClick={loadForumData} className={cl.retryButton}>
                            <IconRefresh width={14} height={14}/>
                            <span>Попробовать снова</span>
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const totalTopics = forumStats.total_topics || 0;
    const totalComments = forumStats.total_comments || 0;
    const recentActivity = forumStats.recent_activity || [];

    return (
        <div className={cl.page_wrapper}>
            <div className={cl.wrap_content_1}>
                {/* Шапка страницы */}
                <div className={cl.pageHeaderV2}>
                    <div className={cl.pageHeaderLeft}>
                        <div className={cl.pageHeaderIcon}>
                            <IconMessage width={22} height={22}/>
                        </div>
                        <div className={cl.pageHeaderText}>
                            <div className={cl.pageHeaderTopRow}>
                                <h1 className={cl.pageHeaderTitle}>Форум</h1>
                                {totalTopics > 0 && (
                                    <span className={cl.pageHeaderPill}>
                                        <span className={cl.pageHeaderPillStrong}>{totalTopics}</span>
                                        <span className={cl.pageHeaderPillMuted}>
                                            {pluralRu(totalTopics, 'тема', 'темы', 'тем')}
                                        </span>
                                    </span>
                                )}
                                {totalComments > 0 && (
                                    <span className={cl.pageHeaderPill}>
                                        <span className={cl.pageHeaderPillStrong}>{totalComments}</span>
                                        <span className={cl.pageHeaderPillMuted}>
                                            {pluralRu(totalComments, 'ответ', 'ответа', 'ответов')}
                                        </span>
                                    </span>
                                )}
                            </div>
                            <div className={cl.breadcrumbs}>
                                АдминПанель · Материалы · Форум
                            </div>
                        </div>
                    </div>
                    <div className={cl.pageHeaderActions}>
                        <button
                            onClick={loadForumData}
                            className={cl.btnGhostV2}
                            title="Обновить"
                        >
                            <IconRefresh width={15} height={15}/>
                        </button>
                        <button
                            onClick={() => navigate('/main/forum/my-topics')}
                            className={cl.btnSecondaryV2}
                        >
                            <IconUser width={15} height={15}/>
                            <span>Мои вопросы</span>
                        </button>
                        <button
                            onClick={() => setShowCreateModal(true)}
                            className={cl.btnPrimaryV2}
                        >
                            <IconPlus width={16} height={16}/>
                            <span>Задать вопрос</span>
                        </button>
                    </div>
                </div>

                {/* Подсказка о странице */}
                <PageIntro
                    storageKey="forum"
                    title="О странице «Форум»"
                    text="Здесь общаются владельцы детских центров, преподаватели и разработчики платформы. Можно задать вопрос, поделиться опытом, предложить новую фичу или обсудить кейс с другими школами."
                    bullets={[
                        'Слева — разделы форума. Зайдите в нужный, чтобы увидеть темы и ответить',
                        '«Задать вопрос» — открывает форму создания темы в любом разделе',
                        '«Мои вопросы» — ваши собственные темы и подписки на чужие',
                        'Будьте вежливы, оставайтесь в рамках темы, не размещайте спам',
                    ]}
                />

                {/* Двухколоночная сетка */}
                <div className={cl.grid}>
                    {/* Левая — категории */}
                    <section className={cl.categoriesSection}>
                        <div className={cl.sectionTitle}>Разделы форума</div>
                        {categories.length === 0 ? (
                            <div className={cl.emptyCard}>Разделы пока не созданы</div>
                        ) : (
                            <div className={cl.categoriesGrid}>
                                {categories.map((category, idx) => (
                                    <CategoryCard
                                        key={category.id}
                                        category={category}
                                        tone={CATEGORY_TONES[idx % CATEGORY_TONES.length]}
                                        onClick={() => navigate(`/main/forum/categories/${category.id}`)}
                                    />
                                ))}
                            </div>
                        )}
                    </section>

                    {/* Правая — sidebar */}
                    <aside className={cl.sidebar}>
                        {/* Поиск (компонент-партнёр, со своими стилями) */}
                        <div className={cl.searchCard}>
                            <ForumSearch />
                        </div>

                        {/* Последние сообщения */}
                        <div className={cl.sideCard}>
                            <div className={cl.sideCardHead}>
                                <span className={cl.sideCardIcon}>
                                    <IconClock width={16} height={16}/>
                                </span>
                                <h3 className={cl.sideCardTitle}>Последние сообщения</h3>
                            </div>
                            {recentActivity.length === 0 ? (
                                <div className={cl.emptyHint}>Пока тихо. Будьте первым!</div>
                            ) : (
                                <div className={cl.recentList}>
                                    {recentActivity.slice(0, 5).map((activity, index) => (
                                        <div key={index} className={cl.recentItem}>
                                            <div className={cl.recentTopic}>
                                                {AdminService.truncateText(activity.topic_title, 60)}
                                            </div>
                                            <div className={cl.recentMeta}>
                                                <span className={cl.recentAuthor}>
                                                    <IconUser width={11} height={11}/>
                                                    {activity.author}
                                                </span>
                                                <span className={cl.recentTime}>
                                                    {AdminService.formatForumDate(activity.created_at)}
                                                </span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* KPI */}
                        <div className={cl.sideCard}>
                            <div className={cl.sideCardHead}>
                                <span className={cl.sideCardIcon}>
                                    <IconMessage width={16} height={16}/>
                                </span>
                                <h3 className={cl.sideCardTitle}>Статистика форума</h3>
                            </div>
                            <div className={cl.statsRow}>
                                <div className={cl.statBox}>
                                    <div className={cl.statValue}>{totalTopics}</div>
                                    <div className={cl.statLabel}>
                                        {pluralRu(totalTopics, 'тема', 'темы', 'тем')}
                                    </div>
                                </div>
                                <div className={cl.statBox}>
                                    <div className={cl.statValue}>{totalComments}</div>
                                    <div className={cl.statLabel}>
                                        {pluralRu(totalComments, 'ответ', 'ответа', 'ответов')}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </aside>
                </div>
            </div>

            {showCreateModal && (
                <CreateTopicModal
                    categories={categories}
                    onClose={() => setShowCreateModal(false)}
                    onTopicCreated={handleTopicCreated}
                />
            )}
        </div>
    );
};


// ----- Карточка категории -----
const CategoryCard = ({ category, tone, onClick }) => {
    return (
        <button
            type="button"
            className={`${cl.categoryCard} ${cl[`categoryCard_${tone}`]}`}
            onClick={onClick}
        >
            <div className={cl.categoryCardBody}>
                <div className={cl.categoryCardHead}>
                    <h3 className={cl.categoryTitle}>{category.name}</h3>
                    <IconArrowRight width={14} height={14} className={cl.categoryArrow}/>
                </div>
                {category.description && (
                    <p className={cl.categoryDescription}>{category.description}</p>
                )}
                <div className={cl.categoryFooter}>
                    <span className={cl.categoryTopicCount}>
                        <IconMessage width={11} height={11}/>
                        {category.topics_count || 0} {pluralRu(category.topics_count || 0, 'тема', 'темы', 'тем')}
                    </span>
                    {category.last_comment?.author && (
                        <span className={cl.categoryLast}>
                            <IconUser width={11} height={11}/>
                            <span className={cl.categoryLastAuthor}>{category.last_comment.author}</span>
                            <span className={cl.categoryLastTime}>
                                · {AdminService.formatForumDate(category.last_comment.created_at)}
                            </span>
                        </span>
                    )}
                </div>
            </div>
        </button>
    );
};

// Простое русское склонение для числительных (1 тема / 2 темы / 5 тем)
function pluralRu(n, one, few, many) {
    const mod10 = n % 10;
    const mod100 = n % 100;
    if (mod10 === 1 && mod100 !== 11) return one;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
    return many;
}

export default Forum;
